// Sync engine — drains locally-captured booths to the backend when online.
//
// iOS Safari does NOT support the Background Sync API (SyncManager), so we can't
// rely on the service worker to flush in the background. Instead we drain on
// every foreground opportunity: app start, the `online` event, tab becoming
// visible, and a manual "Sync now" tap. The nightly habit + manual button are
// the durable backstop against iOS PWA storage eviction (per the brief).
//
// Order per booth: upload unsynced photos first (so the booth record we push
// carries their server URLs), then upsert the record, then mark it synced. A
// failure on any booth is isolated — we move on and retry it next drain.

import {
  listUnsyncedBooths,
  getBooth,
  getPhotosForBooth,
  markBoothSynced,
  markPhotoSynced,
  mergeRemoteBooths,
} from '../db/booths';
import {
  listUnsyncedHitList,
  markHitListSynced,
  mergeRemoteHitList,
} from '../db/hitlist';
import { photoBlob } from '../db/schema';
import { pushBooth, pushPhoto, fetchBooths, pushHitListTarget, fetchHitList, NotAuthedError } from './api';
import { getIdentity } from '../auth/auth';

/** Hit-list leg of a drain (v0.7) — counted separately from booths. */
export interface HitListSyncResult {
  pushed: number;
  pulled: number;
  failed: number;
}

export interface SyncResult {
  attempted: number;
  synced: number;
  failed: number;
  /** Remote records pulled down and merged locally this drain. */
  pulled: number;
  /** Shared hit-list stars pushed/pulled this drain (v0.7). */
  hitlist: HitListSyncResult;
  skipped: 'offline' | 'signed-out' | 'busy' | null;
  /** Last push/pull failure (name: message), for on-device diagnosability. */
  error?: string;
}

const noHitList = (): HitListSyncResult => ({ pushed: 0, pulled: 0, failed: 0 });

export type SyncListener = (state: { running: boolean; result?: SyncResult }) => void;

let running = false;
const listeners = new Set<SyncListener>();

export function onSyncState(fn: SyncListener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function emit(state: { running: boolean; result?: SyncResult }): void {
  for (const fn of listeners) fn(state);
}

export function isSyncing(): boolean {
  return running;
}

/** Drain the local queue once. Safe to call often; no-ops when offline/busy. */
export async function syncNow(): Promise<SyncResult> {
  if (running) return { attempted: 0, synced: 0, failed: 0, pulled: 0, hitlist: noHitList(), skipped: 'busy' };
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    return { attempted: 0, synced: 0, failed: 0, pulled: 0, hitlist: noHitList(), skipped: 'offline' };
  }
  if (!getIdentity().authenticated) {
    return { attempted: 0, synced: 0, failed: 0, pulled: 0, hitlist: noHitList(), skipped: 'signed-out' };
  }

  running = true;
  emit({ running: true });
  const result: SyncResult = { attempted: 0, synced: 0, failed: 0, pulled: 0, hitlist: noHitList(), skipped: null };

  try {
    // Push: drain locally-captured booths up to the backend.
    const pending = await listUnsyncedBooths();
    for (const booth of pending) {
      result.attempted++;
      try {
        // 1) photos first — rebuild a Blob from the stored buffer for upload.
        const photos = await getPhotosForBooth(booth.id);
        for (const p of photos) {
          if (p.synced) continue;
          const url = await pushPhoto(booth.id, p.local_id, photoBlob(p));
          await markPhotoSynced(p.local_id, url);
        }
        // 2) push the (now URL-bearing) record, then mark synced
        const fresh = (await getBooth(booth.id)) ?? booth;
        await pushBooth(fresh);
        await markBoothSynced(booth.id);
        result.synced++;
      } catch (err) {
        result.failed++;
        result.error = errText(err);
        if (err instanceof NotAuthedError) {
          // Token gone (e.g. dropped offline mid-drain) — stop; retry next trigger.
          result.skipped = 'signed-out';
          break;
        }
        // Don't swallow it: on a device this is the only trace of why the booth
        // stayed "on device". pushBooth's message includes the HTTP status + body.
        console.error('sync push failed for booth', booth.id, err);
      }
    }

    // Pull: merge remote booths down so this device sees other devices' captures.
    // A pull failure never fails the push result — log it and move on.
    try {
      const remote = await fetchBooths();
      result.pulled = await mergeRemoteBooths(remote);
    } catch (err) {
      if (err instanceof NotAuthedError) result.skipped ??= 'signed-out';
      result.error ??= errText(err);
      console.error('sync pull failed', err);
    }

    // Hit list (v0.7) — its own leg, deliberately AFTER booths and fully isolated:
    // booths + photos are the irreplaceable artifact, and a hit-list hiccup must
    // never cost a capture its drain.
    result.hitlist = await syncHitList();

    return result;
  } finally {
    running = false;
    emit({ running: false, result });
  }
}

/**
 * Drain + merge the shared hit list. Push each locally-changed star (tombstones
 * included — an un-star has to travel), then pull the whole list and merge LWW.
 * Swallows its own errors: the caller reports counts, and a failed star retries on
 * the next drain exactly like a booth does.
 */
async function syncHitList(): Promise<HitListSyncResult> {
  const out = noHitList();
  try {
    for (const target of await listUnsyncedHitList()) {
      try {
        await pushHitListTarget(target);
        await markHitListSynced(target.exhibitor_key);
        out.pushed++;
      } catch (err) {
        out.failed++;
        console.error('hit-list push failed for', target.exhibitor_key, err);
        if (err instanceof NotAuthedError) return out; // token gone — retry next drain
      }
    }
    out.pulled = await mergeRemoteHitList(await fetchHitList());
  } catch (err) {
    out.failed++;
    console.error('hit-list sync failed', err);
  }
  return out;
}

function errText(err: unknown): string {
  return err instanceof Error ? `${err.name}: ${err.message}` : String(err);
}

/**
 * Pull remote booths and merge them locally, without pushing. Used on list open
 * so the cross-device view refreshes even between drains. No-ops (returns 0)
 * when offline or signed-out; swallows/logs its own errors.
 */
export async function pullBooths(): Promise<number> {
  if (typeof navigator !== 'undefined' && !navigator.onLine) return 0;
  if (!getIdentity().authenticated) return 0;
  try {
    const remote = await fetchBooths();
    return await mergeRemoteBooths(remote);
  } catch (err) {
    console.error('pull failed', err);
    return 0;
  }
}

/**
 * Pull the shared hit list and merge it locally, without pushing (v0.7). Used when
 * the directory / hit-list view opens so a teammate's star shows up between drains.
 * Same contract as pullBooths: no-ops offline or signed-out, swallows its own errors.
 */
export async function pullHitList(): Promise<number> {
  if (typeof navigator !== 'undefined' && !navigator.onLine) return 0;
  if (!getIdentity().authenticated) return 0;
  try {
    return await mergeRemoteHitList(await fetchHitList());
  } catch (err) {
    console.error('hit-list pull failed', err);
    return 0;
  }
}

/**
 * Wire foreground sync triggers. Returns a disposer. Debounced so the burst of
 * online+visibility+focus events on iOS resume doesn't fire several drains.
 */
export function installSyncTriggers(): () => void {
  let timer: ReturnType<typeof setTimeout> | null = null;
  const kick = (): void => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => void syncNow(), 400);
  };

  const onVisible = (): void => {
    if (document.visibilityState === 'visible') kick();
  };

  window.addEventListener('online', kick);
  window.addEventListener('focus', kick);
  document.addEventListener('visibilitychange', onVisible);

  kick(); // initial attempt on load

  return () => {
    if (timer) clearTimeout(timer);
    window.removeEventListener('online', kick);
    window.removeEventListener('focus', kick);
    document.removeEventListener('visibilitychange', onVisible);
  };
}
